import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {  
  Save, Search, Tag, Box, Trash2, ArrowLeft, List,
  Database, Plus, Wrench, Layers, History, Paperclip, CreditCard, ArrowRightLeft, ChevronLeft, ChevronRight, Type, Minus
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext'; // 🎯 للتحكم في حجم الخط

// --- Helpers من هوية النظام ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, onSearch, type = "text", options = [], isRTL }) => (
  <div className={`flex flex-col border-gray-200 px-4 last:border-0 ${isRTL ? 'border-l' : 'border-r'}`}>
    <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{label}</label>
    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {type === "select" ? (
            <select 
                className={`${width} outline-none py-1 font-black uppercase text-blue-900 bg-transparent text-[11px] cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}
                value={value}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
            >
                {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
        ) : (
            <input className={`${width} border-b border-transparent outline-none py-1 font-black uppercase ${isBold ? 'text-blue-900 text-[12px]' : 'text-blue-700'} bg-transparent transition-all focus:border-blue-500 ${disabled ? 'opacity-50' : ''} ${isRTL ? 'text-right' : 'text-left'}`} 
                   value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
        )}
        {onSearch && <Search size={12} className="text-blue-600 cursor-pointer" onClick={onSearch} />}
    </div>
  </div>
);

const InventoryUsage = ({ siteId: propSiteId, orgId: propOrgId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { canCreate, canUpdate, canDelete } = usePermissions('inventoryusage');
  
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  
  // جلب بيانات السيشن فوراً
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || propSiteId || '';
  const sessionOrg = session.org_id || propOrgId || '';

  const [fieldMaps, setFieldMaps] = useState({}); 
  const [activeTab, setActiveTab] = useState('USAGE');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0); 
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const [data, setData] = useState({
    invusenum: '', 
    description: '', 
    status: 'ENTERED', 
    fromstore: '', 
    tostore: '', 
    ref_mr: '',
    usage_type: 'ISSUE', 
    siteid: sessionSite, 
    orgid: sessionOrg, 
    usage_lines: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', filters: {}, targetIdx: null });
  const isReadOnly = data.status === 'COMPLETE' || data.status === 'COMPLETED';

  // --- مكون التحكم في الخط ---
  const FontControls = () => {
    return (
      <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
          <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
        </div>
        <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
      </div>
    );
  };

  const loadData = useCallback(async () => {
    try {
      const resMap = await axios.get('/api/admin/field-map/invusage');
      setFieldMaps(resMap.data || {});

      if (isNew) {
          const resNum = await axios.get(`/api/inventory/usage/next-number/${sessionOrg}`);
          setData(prev => ({ ...prev, invusenum: resNum.data.next_num }));
          return;
      }
      
      setLoading(true);
      const res = await axios.get(`/api/inventory/usage/${id}`);
      
      setData(prev => ({
        ...res.data,
        siteid: res.data.siteid || sessionSite,
        orgid: res.data.orgid || sessionOrg,
        usage_type: res.data.usage_type || 'ISSUE'
      }));
    } catch (err) {
      toast.error(t('common.load_error', "Error loading document details"));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, sessionSite, sessionOrg, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // --- دالة المرفقات ---
  const fetchCount = useCallback(async () => {
    if (!data.invusenum || isNew) return;
    try {
      const res = await axios.get(`/api/attachments/count/INVUSAGE/${data.invusenum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [data.invusenum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  // --- دالة النافيجيشن ---
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`/api/inventory/usage/${id}/navigation${location.search}`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { 
      console.error("Nav failed", err); 
    }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const goTo = (targetId) => targetId && navigate(`/inventoryusage/${targetId}${location.search}`);

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    const linkedDomain = fieldMaps[fieldName];
    let extraFilters = { site_id: data.siteid, org_id: data.orgid };
    
    if (fieldName === 'itemnum') extraFilters.location = data.fromstore;
    if (fieldName === 'assetnum') extraFilters.site_id = data.siteid;

    setLookup({
      isOpen: true, title, type: linkedDomain ? 'DOMAIN' : defaultType,
      domainId: linkedDomain || '', field: fieldName, filters: extraFilters, targetIdx: lineIdx
    });
  };

  const addNewLine = (type) => {
    if (isReadOnly) return;
    const newLine = {
      invuselineid: null, line_type: type, itemnum: '', description: '', unitcost: 0, quantity: 1, issueunit: '', linecost: 0,
      location: '', assetnum: '', wonum: '', glaccount: '', refby: '', tostoreloc: ''
    };
    setData(prev => ({ ...prev, usage_lines: [...prev.usage_lines, newLine] }));
  };

  const selectReservedItems = async (mrnum) => {
    try {
      const res = await axios.get(`/api/inventory/mr/lines/${mrnum}`);
      const mrLines = res.data;
  
      const newUsageLines = mrLines.map(line => ({
        line_type: line.linetype, itemnum: line.itemnum, description: line.description, unitcost: line.unitcost || 0,
        quantity: line.qty_requested, issueunit: line.issueunit || '', linecost: (line.qty_requested * (line.unitcost || 0)),
        location: line.location || '', assetnum: line.assetnum || '', wonum: line.wonum || '', glaccount: line.glaccount || '', refby: mrnum 
      }));
  
      setData(prev => ({ ...prev, ref_mr: mrnum, usage_lines: [...prev.usage_lines, ...newUsageLines] }));
      toast.success(t('invusage.messages.mr_loaded', { mrnum: mrnum }));
    } catch (err) {
      toast.error(t('invusage.messages.mr_fail', "Failed to load MR items"));
    }
  };

  const updateLine1 = (idx, field, value) => {
    if (isReadOnly) return;
    const newLines = [...data.usage_lines];
    newLines[idx][field] = value;
    if (field === 'quantity' || field === 'unitcost') {
        newLines[idx].linecost = (parseFloat(newLines[idx].quantity) || 0) * (parseFloat(newLines[idx].unitcost) || 0);
    }
    setData({...data, usage_lines: newLines});
  };
  
  const updateLine = async (idx, field, value) => {
    if (isReadOnly) return;
    
    if (field === 'quantity') {
        try {
            const invRes = await axios.get(`/api/inventory/balance/${data.usage_lines[idx].itemnum}/${data.fromstore}`);
            const availableQty = invRes.data.curbal;
            if (parseFloat(value) > availableQty) {
                Swal.fire({
                    title: t('invusage.messages.insufficient_title', 'Insufficient Stock!'),
                    text: `${t('invusage.messages.available', 'Available')}: ${availableQty}. ${t('invusage.messages.create_pr', 'Create PR?')}`,
                    icon: 'warning',
                    showCancelButton: true, confirmButtonText: t('invusage.messages.btn_create_pr', 'Create PR'),
                    customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
                });
            }
        } catch (err) { console.error("Balance check failed"); }
    }
    
    updateLine1(idx, field, value);
  };
  
  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
  }, []);
    
  useEffect(() => {
    const openStatusModal = () => {
      if (data.status === 'COMPLETED' || data.status === 'COMPLETE') {
        return Swal.fire({
          title: t('invusage.messages.locked_title', 'Record Locked'),
          text: t('invusage.messages.locked_text', 'This transaction is COMPLETED and cannot be modified or reversed.'),
          icon: 'lock', confirmButtonColor: '#1a3353',
          customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
        });
      }
      setShowStatusModal(true);
    };

    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [data.status, data.invusenum, t, isRTL]);

  const handleInventoryCompletion = async () => {
      const loadId = toast.loading(t('invusage.messages.processing', "Processing Inventory Adjustments..."));
      try {
          const token = localStorage.getItem('token');
          await axios.post(`/api/inventory/usage/complete/${data.invusenum}`, {}, {
              headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success(t('invusage.messages.inventory_updated', "Inventory levels updated successfully"), { id: loadId });
          loadData(); 
      } catch (err) {
          toast.error(err.response?.data?.detail || t('common.error', "Error adjusting inventory"), { id: loadId });
          loadData();
      }
  };  

  const handleLookupSelect = (row) => {
      if (!row) return;
  
      let val;
      if (lookup.field === 'itemnum') val = row.itemnum;
      else if (lookup.field === 'fromstore' || lookup.field === 'tostore' || lookup.field === 'tostoreloc') val = row.location;
      else if (lookup.field === 'location') val = row.location_id || row.location; 
      else if (lookup.field === 'assetnum') val = row.assetnum;
      else if (lookup.field === 'ref_mr') val = row.mrnum;
      else if (lookup.field === 'wonum') val = row.wonum;
      else val = row.value || row.id || row.site_id; 
  
      setData(prev => {
          if (!prev) return prev;
          
          if (lookup.targetIdx !== null) {
              const idx = lookup.targetIdx;
              const nl = [...(prev.usage_lines || [])];
              if (!nl[idx]) return prev;
  
              if (lookup.field === 'itemnum') {
                  nl[idx].itemnum = val;
                  nl[idx].description = row.description || row.item_ref?.description;
                  nl[idx].unitcost = row.avgcost || 0;
                  nl[idx].issueunit = row.issueunit || row.item_ref?.issueunit;
                  nl[idx].linecost = nl[idx].unitcost * nl[idx].quantity;
              } else {
                  nl[idx][lookup.field] = val;
              }
              return { ...prev, usage_lines: nl };
          } 
          
          return { ...prev, [lookup.field]: val };
      });
  
      if (lookup.field === 'ref_mr') selectReservedItems(val);
      setLookup(p => ({ ...p, isOpen: false }));
  };

  const handleDelete = async () => {
    if (data.status === 'COMPLETED' || data.status === 'COMPLETE') {
      return Swal.fire(t('common.error'), t('invusage.messages.cant_delete_comp', 'Cannot delete a completed transaction!'), 'error');
    }

    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('invusage.messages.delete_warn', "This will permanently delete the draft transaction."),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/inventory/usage/${data.invusenum}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast.success(t('common.delete_success', "Transaction deleted"));
        navigate(`/inventoryusage${location.search}`); 
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.delete_fail', "Deletion failed"));
      }
    }
  };

  const handleSave = async () => {
    if (!data.description || !data.fromstore) return toast.error(t('invusage.messages.req_fields', "Description and Storeroom are required!"));
    if (data.usage_type === 'TRANSFER' && !data.tostore) return toast.error(t('invusage.messages.req_transfer', "To Store is required for transfers!"));
    
    setLoading(true);
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.post(`/api/inventory/usage/save-full`, data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(t('common.save_success', "Transaction committed successfully"), { id: loadId });
      if (isNew) navigate(`/inventoryusage/${res.data.invusenum}${location.search}`);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(t('common.session_expired', "Session expired, please login again"), { id: loadId });
        navigate(`/login`);
      } else {
        toast.error(err.response?.data?.detail || t('common.save_fail', "Save failed"), { id: loadId });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const triggerSave = () => {
        if (data.status === 'COMPLETED' || data.status === 'COMPLETE') {
            return toast.error(t('invusage.messages.cant_save_comp', "Cannot save changes to a completed record"));
        }
        handleSave();
    };
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave, data.status, t]);
  
  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);  
  
  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] font-bold duration-300 transition-[font-size] uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={handleLookupSelect} 
      />

      {showHistoryModal && (
        <StatusHistoryModal 
          appName="INVUSAGE" 
          recordId={data.invusenum} 
          onClose={() => setShowHistoryModal(false)} 
        />
      )}
      <AttachmentsModal 
        isOpen={isDocModalOpen} 
        onClose={() => {setIsDocModalOpen(false); fetchCount();}} 
        ownertable="INVUSAGE" 
        ownerid={data.invusenum}
      />      
      
      {showStatusModal && (
        <StatusModal 
          currentStatus={data.status}
          recordId={data.invusenum}
          appName="INVUSAGE" 
          domainId={fieldMaps['status']} 
          onClose={() => setShowStatusModal(false)}
          onComplete={async () => {
              const token = localStorage.getItem('token');
              try {
                  const res = await axios.get(`/api/inventory/usage/${data.invusenum}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const updatedStatus = res.data.status;
                  if (updatedStatus === 'COMPLETED' || updatedStatus === 'COMPLETE') {
                      await handleInventoryCompletion();
                  } else {
                      setData(res.data); 
                  }
              } catch (err) {
                  loadData();
              }
          }}
        />
      )}

      {/* --- 1. TOP TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/inventoryusage${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
          <button onClick={handleSave} disabled={isReadOnly} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all uppercase">
            <Save size={14} /> {loading ? t('common.saving', 'SAVING...') : t('common.save', 'SAVE')}
          </button>
          )}
          
          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          
          {!isNew && (
          <button onClick={() => {
              if (data.status === 'COMPLETED' || data.status === 'COMPLETE') {
                  return Swal.fire(t('common.error'), t('invusage.messages.locked_text', 'This transaction is COMPLETED and cannot be modified.'), 'lock');
              }
              setShowStatusModal(true);
          }} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.change_status', 'Change Status')}>
            <Tag size={16}/>
          </button>
          )}
          
          {!isNew && (
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.attachments', 'Attachments')}>
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
          </button>
          )}
          
          {!isNew && (
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200" title={t('common.status_history', 'Status History')}>
            <History size={16}/>
          </button>
          )}
          
          {!isNew && canDelete && (
            <button onClick={handleDelete} disabled={isReadOnly} className="p-2 hover:bg-red-500/20 text-red-400 rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}
          
          <FontControls />
        </div>
      </div>

      {/* --- 2. HEADER BAR --- */}
      <div className="bg-white p-4 border-b flex items-center gap-12 shadow-sm z-20 font-black">
          <HeaderField label={t('invusage.fields.usagenum', 'Usage ID')} value={data.invusenum} disabled={!isNew} onChange={v => setData({...data, invusenum: v})} width="w-24" isBold isRTL={isRTL} />
          
          <HeaderField 
            label={t('invusage.fields.type', 'Usage Type')} 
            value={data.usage_type}
            disabled={isReadOnly}
            width="w-32" 
            type="select" 
            onChange={v => setData({...data, usage_type: v, tostore: ''})}
            options={[
                {label: t('invusage.types.issue', 'Issue'), value: 'ISSUE'},
                {label: t('invusage.types.transfer', 'Transfer'), value: 'TRANSFER'},
                {label: t('invusage.types.return', 'Return'), value: 'RETURN'}
            ]}
            isRTL={isRTL}
          />

          <HeaderField label={t('common.description', 'Description')} value={data.description} disabled={isReadOnly} onChange={v => setData({...data, description: v})} width="w-60" isRTL={isRTL} />
          <HeaderField label={t('invusage.fields.fromstore', 'From Store')} value={data.fromstore} disabled={isReadOnly} onSearch={() => handleSearch('fromstore', t('invusage.lookup.store', 'Storerooms'), 'storerooms')} width="w-32" isRTL={isRTL} />
          
          {data.usage_type === 'TRANSFER' && (
            <HeaderField label={t('invusage.fields.tostore', 'To Store')} value={data.tostore} disabled={isReadOnly} onSearch={() => handleSearch('tostore', t('invusage.lookup.dest_store', 'Destination Stores'), 'storerooms')} width="w-32" isBold isRTL={isRTL} />
          )}

          <HeaderField label={t('common.status', 'Status')} value={data.status} disabled width="w-20" isRTL={isRTL} />
          <HeaderField label={t('invusage.fields.ref_mr', 'Ref MR#')} value={data.ref_mr} width="w-24" disabled={true} onChange={v => setData({...data, ref_mr: v})} isRTL={isRTL} />
      </div>

      {/* --- 3. Tabs --- */}
      <div className="flex bg-gray-50 border-b border-gray-200 text-gray-500 shadow-inner">
        <button onClick={() => setActiveTab('USAGE')} className={`px-10 py-3 flex items-center gap-2 font-black border-b-4 transition-all ${activeTab === 'USAGE' ? 'border-[#1a3353] text-[#1a3353] bg-white' : 'border-transparent hover:text-gray-800'}`}>
            <Layers size={14}/> {t('invusage.tabs.lines', 'USAGE LINES')}
        </button>
        <button onClick={() => setActiveTab('LOG')} className={`px-10 py-3 flex items-center gap-2 font-black border-b-4 transition-all ${activeTab === 'LOG' ? 'border-[#1a3353] text-[#1a3353] bg-white' : 'border-transparent hover:text-gray-800'}`}>
            <History size={14}/> {t('invusage.tabs.log', 'TRANSACTION LOG')}
        </button>
      </div>

      {/* --- 4. Content Area --- */}
      <div className="flex-1 overflow-y-auto p-6 bg-[#f8f9fb] custom-scrollbar">
        <div className="max-w-7xl mx-auto bg-white border border-gray-100 rounded shadow-xl overflow-hidden min-h-[500px]">
            
            {activeTab === 'USAGE' && (
            <>
            <div className="bg-slate-50 p-4 border-b flex gap-4 px-8 items-center">
                <span className={`text-[11px] font-black text-blue-900 italic tracking-widest flex items-center gap-2 ${isRTL ? 'ml-auto' : 'mr-auto'}`}>
                    <Box size={16} className="text-orange-500"/> {t(`invusage.types.${data.usage_type?.toLowerCase()}`, data.usage_type)} {t('invusage.details.summary', 'Lines Summary')}
                </span>
                <button onClick={() => addNewLine('ITEM')} disabled={isReadOnly} className="bg-white text-blue-700 px-5 py-2 rounded-sm font-black text-[10px] flex items-center gap-2 border border-blue-200 hover:bg-blue-700 hover:text-white transition-all shadow-sm"><Plus size={14}/> {t('invusage.actions.add_item', 'Add Item')}</button>
                <button onClick={() => addNewLine('PART')} disabled={isReadOnly} className="bg-white text-orange-700 px-5 py-2 rounded-sm font-black text-[10px] flex items-center gap-2 border border-orange-200 hover:bg-orange-700 hover:text-white transition-all shadow-sm"><Wrench size={14}/> {t('invusage.actions.add_part', 'Add Part')}</button>
                <button 
                    onClick={() => {
                        if(!data.fromstore) return toast.error(t('invusage.messages.req_fromstore', "Please select a Storeroom first!"));
                        setLookup({
                            isOpen: true, title: t('invusage.lookup.mr', 'Select Approved MR'), type: 'materialrequest', field: 'ref_mr',
                            filters: { storeloc: data.fromstore, status: 'APPR' }
                        });
                    }} 
                    disabled={isReadOnly}
                    className="bg-[#1a3353] text-white px-5 py-2 rounded-sm font-black text-[10px] flex items-center gap-2 hover:bg-black transition-all shadow-md active:scale-95"
                >
                    <Database size={14}/> {t('invusage.actions.select_mr', 'SELECT MR')}
                </button>
            </div>

            {data.usage_lines.map((line, idx) => (
                <div key={idx} className={`border-b border-gray-100 last:border-0 hover:bg-blue-50/10 transition-all p-8 flex flex-col gap-6 animate-in fade-in duration-300 ${isRTL ? 'text-right' : 'text-left'}`}>
                    
                    {/* SECTION 1: LINE ITEM INFO */}
                    <div className="grid grid-cols-12 gap-8 items-start">
                        <div className="col-span-1 flex flex-col items-center justify-center pt-2">
                            <span className="bg-gray-200 text-gray-600 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black">{idx + 1}</span>
                            <button onClick={() => { const nl = [...data.usage_lines]; nl.splice(idx,1); setData({...data, usage_lines: nl}); }} disabled={isReadOnly} className="mt-4 text-gray-300 hover:text-red-600 hover:bg-red-50 p-2 rounded transition-colors disabled:opacity-50">
                                <Trash2 size={16}/>
                            </button>
                        </div>
                        
                        <div className={`col-span-11 grid grid-cols-5 gap-8 bg-slate-50/50 p-5 rounded border border-gray-200 shadow-inner ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div className="flex flex-col border-b border-gray-300 pb-1">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter italic">{t('invusage.fields.stock_item', 'Stock Item')}</label>
                                <div className={`flex justify-between items-center h-7 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <span className="font-black text-blue-900 truncate text-[12px]">{line.itemnum || t('invusage.placeholders.select_item', 'SELECT ITEM...')}</span>
                                    {!isReadOnly && <Search size={14} className="text-blue-500 cursor-pointer hover:scale-110" onClick={() => handleSearch('itemnum', t('invusage.lookup.item', 'Inventory Lookup'), 'items', idx)} />}
                                </div>
                                <span className="text-[9px] text-gray-500 truncate uppercase mt-1 italic font-bold">{line.description || t('common.no_description', 'No description')}</span>
                            </div>

                            <div className="flex flex-col border-b border-gray-300 pb-1">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">{t('invusage.fields.issueunit', 'Issue Unit')}</label>
                                <span className="font-black text-gray-700 h-7 pt-1.5 uppercase text-[11px]">{line.issueunit || '---'}</span>
                            </div>

                            <div className="flex flex-col border-b border-blue-400 pb-1 bg-blue-50/50 px-2 rounded-t">
                                <label className="text-[9px] font-black text-blue-700 uppercase tracking-tighter italic">{t('invusage.fields.qty', 'Quantity')}</label>
                                <input type="number" className={`bg-transparent font-black text-blue-900 outline-none h-7 text-[13px] ${isRTL ? 'text-right' : 'text-left'}`} value={line.quantity} disabled={isReadOnly} onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                            </div>

                            <div className="flex flex-col border-b border-gray-300 pb-1">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter font-mono">{t('invusage.fields.unitcost', 'Unit Cost')}</label>
                                <span className="font-black text-gray-500 h-7 pt-1.5 font-mono text-[11px]">{parseFloat(line.unitcost || 0).toFixed(2)}</span>
                            </div>

                            <div className="flex flex-col border-b border-green-500 pb-1 bg-green-50/50 px-2 rounded-t font-mono">
                                <label className="text-[9px] font-black text-green-700 uppercase tracking-tighter">{t('invusage.fields.linecost', 'Total Line Value')}</label>
                                <span className="font-black text-green-800 h-7 pt-1.5 text-[13px] italic">{(line.quantity * line.unitcost).toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 2: DYNAMIC CHARGE/TRANSFER INFO */}
                    <div className="grid grid-cols-12 gap-8 items-start">
                        <div className="col-span-1"></div>
                        <div className="col-span-11 grid grid-cols-4 gap-8 bg-white border border-gray-200 p-6 rounded shadow-sm relative pt-8 transition-all">
                            <div className={`absolute -top-3 bg-gray-100 text-gray-600 border border-gray-200 px-5 py-1 text-[9px] font-black uppercase rounded shadow-sm tracking-[0.1em] flex items-center gap-2 ${isRTL ? 'right-6 flex-row-reverse' : 'left-6'}`}>
                                <CreditCard size={12}/> 
                                {data.usage_type === 'TRANSFER' ? t('invusage.details.transfer_dest', 'Transfer Destination Details') : t('invusage.details.financial', 'Financial Charging & Reference')}
                            </div>
                            
                            {data.usage_type === 'TRANSFER' ? (
                                <>
                                    <div className="flex flex-col border-b border-gray-200 col-span-2">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter italic">{t('invusage.fields.target_bin', 'Target Bin in')} {data.tostore}</label>
                                        <input 
                                            className={`outline-none h-7 font-black text-blue-900 bg-transparent uppercase ${isRTL ? 'text-right' : 'text-left'}`} 
                                            placeholder={t('invusage.placeholders.dest_bin', 'ENTER DESTINATION BIN...')}
                                            value={line.tostoreloc || ''}
                                            disabled={isReadOnly}
                                            onChange={e => updateLine(idx, 'tostoreloc', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-span-2 flex items-center justify-center border border-dashed border-gray-200 rounded p-2 text-gray-400 bg-gray-50 italic">
                                        {t('invusage.messages.balance_move', 'Note: Balance will be moved to')} <span className="font-bold text-blue-900 mx-1">{data.tostore || '---'}</span> {t('invusage.messages.upon_comp', 'upon completion.')}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex flex-col border-b border-gray-200 group focus-within:border-blue-500 transition-all">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter italic">{t('invusage.fields.target_loc', 'Target Loc')}</label>
                                        <div className={`flex justify-between items-center h-7 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <span className="font-black text-blue-900 truncate text-[11px] uppercase">{line.location || t('invusage.placeholders.choose_loc', 'CHOOSE LOCATION')}</span>
                                            {!isReadOnly && <Search size={14} className="text-blue-500 cursor-pointer" onClick={() => handleSearch('location', t('invusage.lookup.loc', 'Site Locations'), 'locations', idx)} />}
                                        </div>
                                    </div>

                                    <div className="flex flex-col border-b border-gray-200 group focus-within:border-orange-500 transition-all">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter italic">{t('invusage.fields.assetnum', 'Asset Number')}</label>
                                        <div className={`flex justify-between items-center h-7 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <span className="font-black text-orange-700 truncate text-[11px] uppercase">{line.assetnum || t('invusage.placeholders.select_asset', 'SELECT ASSET')}</span>
                                            {!isReadOnly && <Search size={14} className="text-orange-500 cursor-pointer" onClick={() => handleSearch('assetnum', t('invusage.lookup.asset', 'Asset Registry'), 'assets', idx)} />}
                                        </div>
                                    </div>

                                    <div className="flex flex-col border-b border-gray-200 group focus-within:border-blue-700 transition-all">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter italic">{t('invusage.fields.wonum', 'Work Order Reference')}</label>
                                        <div className={`flex justify-between items-center h-7 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <span className="font-black text-blue-600 truncate text-[11px] uppercase">{line.wonum || t('invusage.placeholders.charge_wo', 'CHARGE TO WO')}</span>
                                            {!isReadOnly && <Search size={14} className="text-blue-500 cursor-pointer" onClick={() => handleSearch('wonum', t('invusage.lookup.wo', 'Work Orders'), 'workorders', idx)} />}
                                        </div>
                                    </div>

                                    <div className="flex flex-col border-b border-gray-200 group">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-tighter italic">{t('invusage.fields.glaccount', 'Debit GL Account')}</label>
                                        <div className={`flex justify-between items-center h-7 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <span className="font-mono font-bold text-gray-500 truncate text-[11px]">{line.glaccount || '---'}</span>
                                            {!isReadOnly && <List size={14} className="text-blue-300 cursor-pointer hover:text-blue-600" />}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            </>
            )}

            {/* TAB: LOG */}
            {activeTab === 'LOG' && (
                <div className="p-8">
                    <p className="text-gray-400 italic text-center p-10">{t('common.no_logs_yet', 'No logs available yet')}</p>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default InventoryUsage;